/**
 * ClockMantel - the clocks, under the header, in every mode (v9.5.11).
 *
 * A clock is pressure only while it is seen, and the clocks panel lives in
 * the Story column, which Stealth and Combat put behind another pane. This
 * strip shows every clock the viewer may see as a small dial - the name,
 * the count, a ring cut into its segments - whatever the mode. It takes
 * no room when there is no clock.
 *
 * Whoever directs fills a segment by clicking a dial, and empties one with
 * a right click; everything else about a clock (its size, who sees it, how
 * it runs) stays in the Story column's panel. A countdown's ring shows
 * what is LEFT, and its count reads "3 left". A kept clock (the GM's
 * alone) wears a dashed edge; one shown to named people says so.
 *
 * @param {object} props
 * @param {import('../../hooks/useStoryBoards').Clocks} props.clocks
 * @param {boolean} props.canDirect
 * @param {{uid: string, name: string}[]} [props.people] - to name who a clock is shown to
 */
import styles from '../../styles/ClockMantel.module.css';
import { countText, isFull, remainingOf } from '../../engine/narrative/clocks';

/** Who sees it, in a word or two: nothing for the whole table. */
function audienceWord(clock, people) {
  if (!clock.visible) return 'kept';
  if (clock.audience.includes('*')) return '';
  if (clock.audience.includes('@players')) return 'players';
  const names = clock.audience.map((uid) => people.find((p) => p.uid === uid)?.name).filter(Boolean);
  return names.length === 1 ? names[0] : `${clock.audience.length} people`;
}

/** The ring: the lit share, and the angle of one segment (for the cuts between them). */
function ringStyle(clock) {
  const lit = clock.direction === 'down' ? remainingOf(clock) : clock.filled;
  return { '--mantel-lit': `${(lit / clock.segments) * 100}%`, '--mantel-seg': `${360 / clock.segments}deg` };
}

function Dial({ clock, people }) {
  const word = audienceWord(clock, people);
  return (
    <>
      <span className={styles.ring} style={ringStyle(clock)} aria-hidden="true" />
      <span className={styles.words}>
        <span className={styles.name}>{clock.name}</span>
        <span className={styles.count}>{countText(clock)}</span>
      </span>
      {word && (
        <span className={styles.audience} title={word === 'kept' ? 'Kept: the GM alone sees it' : `Shown to ${word}`}>
          {word}
        </span>
      )}
    </>
  );
}

function MantelClock({ clock, clocks, canDirect, people }) {
  const meter = {
    role: 'meter',
    'aria-label': clock.name,
    'aria-valuemin': 0,
    'aria-valuemax': clock.segments,
    'aria-valuenow': clock.filled,
    'aria-valuetext': countText(clock),
  };
  const marks = {
    'data-testid': `mantel-clock-${clock.id}`,
    'data-direction': clock.direction,
    'data-full': isFull(clock) || undefined,
    'data-kept': !clock.visible || undefined,
  };
  if (!canDirect) {
    return (
      <div className={styles.clock} {...meter} {...marks}>
        <Dial clock={clock} people={people} />
      </div>
    );
  }
  return (
    <div className={styles.clock} {...marks}>
      <button
        type="button"
        className={styles.fill}
        {...meter}
        data-testid={`mantel-fill-${clock.id}`}
        title="Click fills a segment; right click empties one"
        onClick={() => clocks.advance(clock.id, 1)}
        onContextMenu={(e) => {
          e.preventDefault();
          clocks.advance(clock.id, -1);
        }}
      >
        <Dial clock={clock} people={people} />
      </button>
    </div>
  );
}

export default function ClockMantel({ clocks, canDirect, people = [] }) {
  if (clocks.clocks.length === 0) return null;
  return (
    <div className={styles.mantel} role="group" aria-label="Clocks" data-testid="clock-mantel">
      {clocks.clocks.map((clock) => (
        <MantelClock key={clock.id} clock={clock} clocks={clocks} canDirect={canDirect} people={people} />
      ))}
    </div>
  );
}
